import React, { useMemo, useRef, useState } from "react";
import { Animated, Modal, Pressable, ScrollView, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import { useToast } from "../../components/LhToastContainer";
import { selectOrgTypes } from "../../store/slices/masterDataSlice";
import TeamAssignmentFilterDropdown, {
  TeamAssignmentFilterOption,
} from "./components/TeamAssignmentFilterDropdown";
import styles from "./TeamAssignment.styles";

/**
 * Team Assignment tab — converted from ticketing-portal-web-configs_2.html
 * lines 6322-6393 (#configTab-team): data table listing category / subcategory /
 * tertiary combinations mapped to a Sustain team, with search, per-column
 * filter stubs, sorting, row selection, and pagination.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export type SortDirection = "asc" | "desc" | null;

export type TeamAssignmentColumn =
  | "category"
  | "subcategory"
  | "tertiary"
  | "org"
  | "ag"
  | "team"
  | "status";

export interface TeamAssignmentRow {
  id: string;
  category: string;
  subcategory: string;
  tertiary: string;
  org: string;
  /** SNOW AG (ServiceNow Assignment Group) — read-only, populated by the ServiceNow integration. */
  ag: string;
  team: string;
  /** Derived from the row's own `isActive` (auto-routing on/off), not the assigned team's status. */
  status: "active" | "inactive";
  /** True when the assigned team itself is inactive — distinct from `status` above. */
  teamAssignmentInactive?: boolean;
}

export interface TeamEntry {
  key: string;
  name: string;
  /** Short display name (used for pills). Defaults to name when not set. */
  short?: string;
  /** CSS / hex colour for the dot indicator. Defaults to "#838383". */
  color?: string;
  active: boolean;
}

export interface TeamAssignmentProps {
  /** Rows to render, mapped from GET /ticketing/config/team-assignments. */
  rows?: TeamAssignmentRow[];
  /** Prevents row presses from opening the edit assignment modal. */
  readOnly?: boolean;
  /** True while data is being (re)loaded. */
  loading?: boolean;
  /** Teams shown inside the Manage Teams modal, mapped from GET /ticketing/config/teams. */
  teams?: TeamEntry[];
  /** Refreshes the team list from the server before opening Manage Teams. */
  onRefreshTeams?: () => Promise<void> | void;
  /**
   * Fired after the user saves changes in the Manage Teams modal.
   * Receives the updated team list.
   */
  onSaveTeams?: (teams: TeamEntry[]) => void | Promise<void>;
  /** Fired when a row is selected for editing (Organization / Team Assignment). */
  onSelectRow?: (row: TeamAssignmentRow) => void;
  /**
   * Fired when a column filter icon is pressed — stub hook for wiring a
   * dropdown / modal filter UI (mirrors openFilter(this) in the HTML source).
   */
  onOpenFilter?: (column: TeamAssignmentColumn) => void;
  /**
   * Fired when the status toggle is pressed for a row.
   * The caller is responsible for updating the row's status in their data store.
   */
  onToggleStatus?: (row: TeamAssignmentRow, newStatus: "active" | "inactive") => void;
  /** Fired when the Edit Assignment modal is saved (Organization / Team reassignment / Active toggle). */
  onEditAssignment?: (
    row: TeamAssignmentRow,
    updates: { organizationName?: string; teamId?: number; active?: boolean }
  ) => void | Promise<void>;
  /**
   * When set (Sustain Team Member with role "Member"), restricts editing and
   * status-toggling to rows whose team key matches this value.
   * Leads receive `undefined` here (no restriction).
   */
  memberTeamKey?: string;
}

const COLUMN_LABEL: Record<TeamAssignmentColumn, string> = {
  category: "Category",
  subcategory: "Subcategory",
  tertiary: "Tertiary",
  org: "Organization",
  ag: "SNOW AG",
  team: "Team Assignment",
  status: "Status",
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const DEFAULT_SORT_COLUMNS: TeamAssignmentColumn[] = ["category", "subcategory", "tertiary"];

// ── Sortable / filterable column header ──────────────────────────────────────

interface ThProps {
  column: TeamAssignmentColumn;
  style?: object;
  sortColumn: TeamAssignmentColumn | null;
  sortDirection: SortDirection;
  onSort: (col: TeamAssignmentColumn) => void;
  onOpenFilter?: (col: TeamAssignmentColumn) => void;
  filterOptions: TeamAssignmentFilterOption[];
  filterValue: TeamAssignmentFilterOption[];
  onFilterChange: (value: TeamAssignmentFilterOption[] | null) => void;
  centered?: boolean;
}

const Th: React.FC<ThProps> = ({
  column,
  style,
  sortColumn,
  sortDirection,
  onSort,
  onOpenFilter,
  filterOptions,
  filterValue,
  onFilterChange,
  centered,
}) => {
  const isActive = sortColumn === column;
  const arrow = !isActive ? "↕" : sortDirection === "asc" ? "↑" : "↓";
  return (
    <View style={[styles.th, style, centered && { justifyContent: "center" }]}>
      <Pressable style={styles.thSortTarget} onPress={() => onSort(column)}>
        <Text style={styles.thLabel}>{COLUMN_LABEL[column]}</Text>
        <Text style={[styles.sortIndicator, isActive && styles.sortIndicatorActive]}>{arrow}</Text>
      </Pressable>
      <TeamAssignmentFilterDropdown
        label={COLUMN_LABEL[column]}
        options={filterOptions}
        value={filterValue}
        onChange={(value) => {
          onFilterChange(value);
        }}
        onOpen={() => onOpenFilter?.(column)}
      />
    </View>
  );
};

// ── Status toggle (mirrors .switch in HTML) ───────────────────────────────────

interface StatusToggleProps {
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

const StatusToggle: React.FC<StatusToggleProps> = ({ active, disabled, onToggle }) => {
  const translateX = React.useRef(new Animated.Value(active ? 16 : 0)).current;

  React.useEffect(() => {
    Animated.timing(translateX, {
      toValue: active ? 16 : 0,
      duration: 150,
      useNativeDriver: true,
    }).start();
  }, [active]);

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: active, disabled }}
      onPress={() => !disabled && onToggle()}
      style={[
        styles.toggleTrack,
        active ? styles.toggleTrackOn : styles.toggleTrackOff,
        disabled && styles.toggleTrackDisabled,
      ]}
    >
      <Animated.View style={[styles.toggleThumb, { transform: [{ translateX }] }]} />
    </Pressable>
  );
};

// ── Manage Teams Modal ────────────────────────────────────────────────────────

interface ManageTeamsModalProps {
  visible: boolean;
  initialTeams: TeamEntry[];
  onSave: (teams: TeamEntry[]) => void | Promise<void>;
  onClose: () => void;
}

const ManageTeamsModal: React.FC<ManageTeamsModalProps> = ({ visible, initialTeams, onSave, onClose }) => {
  const [teams, setTeams] = useState<TeamEntry[]>([]);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const inputRef = useRef<TextInput>(null);

  // Reset state whenever the modal opens
  React.useEffect(() => {
    if (visible) {
      setTeams(initialTeams.map((t) => ({ ...t })));
      setShowAddRow(false);
      setNewTeamName("");
      setIsDirty(false);
    }
  }, [visible, initialTeams]);

  const trimmed = newTeamName.trim();
  const isDuplicate =
    !!trimmed &&
    teams.some((t) => t.name.trim().toLowerCase() === trimmed.toLowerCase());
  const hasTeamChanges =
    teams.length !== initialTeams.length ||
    teams.some((team) => {
      const initialTeam = initialTeams.find((entry) => entry.key === team.key);
      return (
        !initialTeam ||
        initialTeam.name !== team.name ||
        initialTeam.short !== team.short ||
        initialTeam.color !== team.color ||
        initialTeam.active !== team.active
      );
    });
  const hasValidPendingTeam = trimmed.length > 0 && trimmed.length <= 25 && !isDuplicate;
  const canSave = (hasTeamChanges || hasValidPendingTeam) && !isDuplicate;

  function handleToggleActive(key: string, value: boolean) {
    setTeams((prev) => prev.map((t) => (t.key === key ? { ...t, active: value } : t)));
    setIsDirty(true);
  }

  function handleShowAddRow() {
    setShowAddRow(true);
    // focus is handled by autoFocus on the input
  }

  function handleCommit() {
    if (!trimmed || trimmed.length > 25 || isDuplicate) return;
    const key = "team_" + Date.now().toString(36);
    setTeams((prev) => [
      ...prev,
      { key, name: trimmed, short: trimmed, color: "#838383", active: true },
    ]);
    setNewTeamName("");
    setShowAddRow(false);
    setIsDirty(true);
  }

  async function handleSave() {
    if (!canSave) return;
    // Commit any pending input before saving
    if (trimmed && !isDuplicate) {
      const key = "team_" + Date.now().toString(36);
      const newList: TeamEntry[] = [
        ...teams,
        { key, name: trimmed, short: trimmed, color: "#838383", active: true },
      ];
      await onSave(newList);
    } else {
      await onSave(teams);
    }
  }

  function handleClose() {
    if (isDirty) {
      // Simple guard — callers can swap for a confirm modal if desired
    }
    onClose();
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      {/* Overlay */}
      <Pressable style={styles.modalOverlay} onPress={handleClose}>
        {/* Modal panel — stop propagation so tapping inside doesn't close */}
        <Pressable style={styles.modalPanel} onPress={(e) => e.stopPropagation?.()}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Manage Teams</Text>
            <Pressable style={styles.modalCloseBtn} onPress={handleClose}>
              <MaterialIcons name="close" size={18} color="#4B4B4B" />
            </Pressable>
          </View>

          {/* Body */}
          <ScrollView style={styles.modalBody} contentContainerStyle={{ flexGrow: 1 }}>
            <Text style={styles.modalHelpText}>
              Teams cannot be deleted. Inactive is the only removal path. Character limit: 25.
            </Text>

            {/* Team list */}
            {teams.map((team, idx) => (
              <View
                key={team.key}
                style={[
                  styles.teamRow,
                  idx < teams.length - 1 && styles.teamRowBorder,
                ]}
              >
                {/* Filled pill chip */}
                <View style={[styles.teamPill, { backgroundColor: (team.color ?? "#838383") + "26" }]}>
                  <View style={[styles.teamDot, { backgroundColor: team.color ?? "#838383" }]} />
                  <Text style={[styles.teamPillText, { color: team.color ?? "#838383" }]}>{team.name}</Text>
                </View>
                {/* Active toggle */}
                <StatusToggle
                  active={team.active}
                  onToggle={() => handleToggleActive(team.key, !team.active)}
                />
              </View>
            ))}

            {/* Add new team row */}
            {showAddRow && (
              <View style={styles.addTeamRow}>
                <Text style={styles.addTeamLabel}>New Team Name</Text>
                <TextInput
                  ref={inputRef}
                  autoFocus
                  style={[styles.addTeamInput, isDuplicate && styles.addTeamInputError]}
                  value={newTeamName}
                  onChangeText={setNewTeamName}
                  placeholder="e.g. Facilities Team"
                  placeholderTextColor="#9099aa"
                  maxLength={25}
                  onSubmitEditing={handleCommit}
                  returnKeyType="done"
                />
                {isDuplicate && (
                  <Text style={styles.validationError}>Duplicate Name</Text>
                )}
              </View>
            )}
          </ScrollView>

          {/* Footer */}
          <View style={styles.modalFooter}>
            {/* Cancel — plain text, left-aligned */}
            <Pressable onPress={handleClose} style={styles.modalFooterCancel}>
              <Text style={styles.modalFooterCancelText}>Cancel</Text>
            </Pressable>
            {/* Add — outlined pill, centred */}
            <Pressable style={styles.btnOutlinedPill} onPress={handleShowAddRow}>
              <Text style={styles.btnOutlinedPillText}>Add</Text>
            </Pressable>
            {/* Save — filled slate-blue pill, right-aligned */}
            <Pressable
              style={[styles.btnFilledPill, !canSave && styles.btnFilledPillDisabled]}
              disabled={!canSave}
              onPress={canSave ? handleSave : undefined}
            >
              <Text style={styles.btnFilledPillText}>Save</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ── Edit Assignment Modal ─────────────────────────────────────────────────────

interface EditAssignmentModalProps {
  visible: boolean;
  row: TeamAssignmentRow | null;
  teams: TeamEntry[];
  onClose: () => void;
  onSave: (updates: { organizationName?: string; teamId?: number; active?: boolean }) => void | Promise<void>;
}

const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({ visible, row, teams, onClose, onSave }) => {
  const [org, setOrg] = useState("");
  const [selectedTeamKey, setSelectedTeamKey] = useState<string | null>(null);
  const [active, setActive] = useState(true);
  const [initialOrg, setInitialOrg] = useState("");
  const [initialTeamKey, setInitialTeamKey] = useState<string | null>(null);
  const [initialActive, setInitialActive] = useState(true);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const orgTypes = useSelector(selectOrgTypes);

  React.useEffect(() => {
    if (visible && row) {
      const rowOrg = row.org ?? "";
      const rowTeamKey = teams.find((t) => t.name === row.team)?.key ?? null;
      const rowActive = row.status === "active";
      setOrg(rowOrg);
      setSelectedTeamKey(rowTeamKey);
      setActive(rowActive);
      setInitialOrg(rowOrg);
      setInitialTeamKey(rowTeamKey);
      setInitialActive(rowActive);
      setShowDiscardConfirm(false);
    }
  }, [visible, row, teams]);

  if (!row) return null;

  async function handleSave() {
    await onSave({
      organizationName: org.trim() || undefined,
      teamId: selectedTeamKey && Number.isFinite(Number(selectedTeamKey))
        ? Number(selectedTeamKey)
        : undefined,
      active,
    });
  }

  function requestClose() {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }

  function confirmNavigate() {
    setShowDiscardConfirm(false);
    onClose();
  }

  const activeTeams = teams.filter((team) => team.active);
  const isDirty =
    org.trim() !== initialOrg.trim() ||
    selectedTeamKey !== initialTeamKey ||
    active !== initialActive;
  const hasRequiredSelections = org.trim().length > 0 && selectedTeamKey !== null;
  const canSave = isDirty && hasRequiredSelections;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={requestClose}>
      <Pressable style={[styles.modalOverlay, styles.editModalOverlay]} onPress={requestClose}>
        <Pressable style={[styles.modalPanel, styles.editModalPanel]} onPress={(e) => e.stopPropagation?.()}>
          <View style={[styles.modalHeader, styles.editModalHeader]}>
            <Text style={styles.editModalTitle}>Edit Team Assignment</Text>
            <Pressable style={styles.modalCloseBtn} onPress={requestClose}>
              <MaterialIcons name="close" size={24} color="#656565" />
            </Pressable>
          </View>

          <ScrollView style={[styles.modalBody, styles.editModalBody]} contentContainerStyle={{ flexGrow: 1 }}>
            <View style={styles.editDetailsGrid}>
              <View style={styles.editDetailCell}>
                <Text style={styles.editFieldLabel}>Category</Text>
                <Text style={styles.editFieldValue}>{row.category || "—"}</Text>
              </View>
              <View style={styles.editDetailCell}>
                <Text style={styles.editFieldLabel}>Subcategory</Text>
                <Text style={styles.editFieldValue}>{row.subcategory || "—"}</Text>
              </View>
              <View style={styles.editDetailCell}>
                <Text style={styles.editFieldLabel}>Tertiary</Text>
                <Text style={styles.editFieldValue}>{row.tertiary || "—"}</Text>
              </View>
              <View style={styles.editDetailCell}>
                <Text style={styles.editFieldLabel}>SNOW AG</Text>
                <Text style={styles.editFieldValue}>{row.ag || "—"}</Text>
                <Text style={styles.editReadOnlyText}>Read-only. Populated through the ServiceNow integration.</Text>
              </View>
            </View>

            <View style={styles.editFieldsGrid}>
              <View style={styles.editFieldCell}>
                <Text style={styles.editFieldLabel}>
                  Organization<Text style={{ color: "#D32F2F" }}> *</Text>
                </Text>
                <CrossPlatformDropdown
                  childList={orgTypes.map((orgType) => ({ id: orgType, displayText: orgType }))}
                  placeholder="Select organization"
                  selection="single"
                  isClearable
                  isRequired
                  selectedValue={org ? { id: org, displayText: org } : null}
                  setSelectedValue={(selectedItem) => {
                    setOrg(selectedItem && !Array.isArray(selectedItem) ? String(selectedItem.displayText) : "");
                  }}
                  size="large"
                  disableSorting
                  style={styles.editDropdown}
                />
              </View>
              <View style={styles.editFieldCell}>
                <Text style={styles.editFieldLabel}>
                  Team Assignment<Text style={{ color: "#D32F2F" }}> *</Text>
                </Text>
                <CrossPlatformDropdown
                  childList={activeTeams.map((team) => ({ id: team.key, displayText: team.name }))}
                  placeholder="Select team assignment"
                  selection="single"
                  isClearable
                  isRequired
                  selectedValue={
                    selectedTeamKey
                      ? {
                          id: selectedTeamKey,
                          displayText: teams.find((team) => team.key === selectedTeamKey)?.name ?? "",
                        }
                      : null
                  }
                  setSelectedValue={(selectedItem) => {
                    setSelectedTeamKey(
                      selectedItem && !Array.isArray(selectedItem) ? String(selectedItem.id) : null
                    );
                  }}
                  size="large"
                  disableSorting
                  style={styles.editDropdown}
                />
              </View>
            </View>

            <View style={styles.editStatusRow}>
              <Text style={styles.editFieldLabel}>Status</Text>
              <StatusToggle
                active={active}
                disabled={row.teamAssignmentInactive && !active}
                onToggle={() => setActive((a) => !a)}
              />
            </View>
            {row.teamAssignmentInactive && (
              <Text style={[styles.validationError, styles.editWarning]}>
                Team Assignment Inactive, Reactivate Team or Change Team Assignment
              </Text>
            )}
          </ScrollView>

          <View style={[styles.modalFooter, styles.editModalFooter]}>
            <Pressable onPress={requestClose} style={styles.editModalCancel}>
              <Text style={styles.editModalCancelText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[styles.editModalSave, !canSave && styles.editModalSaveDisabled]}
              disabled={!canSave}
              onPress={canSave ? handleSave : undefined}
            >
              <Text style={styles.editModalSaveText}>Save</Text>
            </Pressable>
          </View>

          {showDiscardConfirm && (
            <View style={styles.editConfirmOverlay}>
              <View style={styles.editConfirmCard}>
                <Text style={styles.editConfirmTitle}>Unsaved changes</Text>
                <Text style={styles.editConfirmMessage}>
                  Navigating away will cause you to lose any unsaved data, are you sure?
                </Text>
                <View style={styles.editConfirmActions}>
                  <Pressable onPress={() => setShowDiscardConfirm(false)} style={styles.editConfirmCancel}>
                    <Text style={styles.editConfirmCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={confirmNavigate} style={styles.editConfirmNavigate}>
                    <Text style={styles.editConfirmNavigateText}>Navigate</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const TeamAssignment: React.FC<TeamAssignmentProps> = ({
  rows = [],
  readOnly = false,
  loading = false,
  teams: teamsProp = [],
  onRefreshTeams,
  onSaveTeams,
  onSelectRow,
  onOpenFilter,
  onToggleStatus,
  onEditAssignment,
  memberTeamKey,
}) => {
  const toast = useToast();
  const [manageTeamsVisible, setManageTeamsVisible] = useState(false);
  const [manageTeamsLoading, setManageTeamsLoading] = useState(false);
  const [editingRow, setEditingRow] = useState<TeamAssignmentRow | null>(null);

  async function handleOpenManageTeams() {
    if (onRefreshTeams) {
      setManageTeamsLoading(true);
      try {
        await onRefreshTeams();
      } finally {
        setManageTeamsLoading(false);
      }
    }
    setManageTeamsVisible(true);
  }

  async function handleSaveTeams(updated: TeamEntry[]) {
    await onSaveTeams?.(updated);
    setManageTeamsVisible(false);
    if (onSaveTeams) {
      toast.show("Team changes saved successfully", "success");
    }
  }

  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState<TeamAssignmentColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [rppOpen, setRppOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Partial<Record<TeamAssignmentColumn, TeamAssignmentFilterOption[]>>>({});

  const filterOptions = useMemo(() => {
    const columns = Object.keys(COLUMN_LABEL) as TeamAssignmentColumn[];
    return columns.reduce<Record<TeamAssignmentColumn, TeamAssignmentFilterOption[]>>((result, column) => {
      const values = new Set<string>();
      rows.forEach((row) => {
        const value = String(row[column] ?? "");
        if (value) values.add(value);
      });

      result[column] = Array.from(values)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          id: value,
          displayText: column === "status" ? value[0].toUpperCase() + value.slice(1) : value,
        }));
      return result;
    }, {} as Record<TeamAssignmentColumn, TeamAssignmentFilterOption[]>);
  }, [rows]);

  function handleFilterChange(column: TeamAssignmentColumn, value: TeamAssignmentFilterOption[] | null) {
    setColumnFilters((previous) => {
      const next = { ...previous };
      if (value && value.length > 0) {
        next[column] = value;
      } else {
        delete next[column];
      }
      return next;
    });
    setCurrentPage(1);
  }

  function matchesColumnFilters(row: TeamAssignmentRow) {
    return (Object.entries(columnFilters) as [TeamAssignmentColumn, TeamAssignmentFilterOption[]][]).every(
      ([column, selected]) => selected.some((option) => String(row[column] ?? "") === String(option.id))
    );
  }

  // Client-side search across category / subcategory / tertiary (mirrors searchConfig(value))
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filteredRows = rows.filter(matchesColumnFilters);
    if (!q) return filteredRows;
    return filteredRows.filter(
      (r) =>
        r.category.toLowerCase().includes(q) ||
        r.subcategory.toLowerCase().includes(q) ||
        r.tertiary.toLowerCase().includes(q)
    );
  }, [rows, search, columnFilters]);

  // Client-side sort (mirrors sortTable(th, colIndex))
  const sorted = useMemo(() => {
    const copy = [...searched];

    if (!sortColumn || !sortDirection) {
      copy.sort((a, b) => {
        for (const column of DEFAULT_SORT_COLUMNS) {
          const cmp = String(a[column] ?? "").localeCompare(String(b[column] ?? ""));
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
      return copy;
    }

    copy.sort((a, b) => {
      const av = String(a[sortColumn] ?? "");
      const bv = String(b[sortColumn] ?? "");
      const cmp = av.localeCompare(bv);
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [searched, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const hasActiveFilters = Object.keys(columnFilters).length > 0;
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, currentPage, rowsPerPage]);

  function handleSort(col: TeamAssignmentColumn) {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  }

  function handleSetRowsPerPage(value: number) {
    setRowsPerPage(value);
    setCurrentPage(1);
  }

  function handleSelectRow(row: TeamAssignmentRow) {
    if (readOnly) return;
    // Members can only edit rows assigned to their own team
    if (memberTeamKey !== undefined) {
      const entry = teamsProp.find((t) => t.name === row.team);
      if (!entry || entry.key !== memberTeamKey) return;
    }
    setSelectedRowId((prev) => (prev === row.id ? null : row.id));
    onSelectRow?.(row);
    setEditingRow(row);
  }

  return (
    <View style={styles.dataTableWrap}>
      {/* ── Toolbar: search + result count + Manage Teams ── */}
      <View style={styles.tableToolbar}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={14} color="#838383" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="Search by category, subcategory, or tertiary"
            placeholderTextColor="#9099aa"
          />
        </View>

        <Text style={styles.resultCount}>
          {sorted.length} of {rows.length} {rows.length === 1 ? "result" : "results"}
        </Text>

        {/* Members cannot manage teams — only Leads can */}
        {memberTeamKey === undefined && (
          <Pressable
            style={[styles.btnSecondary, (readOnly || manageTeamsLoading) && { opacity: 0.6 }]}
            onPress={readOnly || manageTeamsLoading ? undefined : handleOpenManageTeams}
            disabled={readOnly || manageTeamsLoading}
          >
            <MaterialIcons name="group-add" size={14} color="#034895" />
            <Text style={styles.btnSecondaryText}>Manage Teams</Text>
          </Pressable>
        )}
      </View>

      {/* ── Table ── */}
      <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={{ flexGrow: 1 }}>
        <View style={[styles.tableMinWidth, { flexGrow: 1 }]}>
          <View style={styles.tableHeadRow}>
            <Th column="category" style={styles.colCategory} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.category} filterValue={columnFilters.category ?? []} onFilterChange={(value) => handleFilterChange("category", value)} />
            <Th column="subcategory" style={styles.colSubcategory} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.subcategory} filterValue={columnFilters.subcategory ?? []} onFilterChange={(value) => handleFilterChange("subcategory", value)} />
            <Th column="tertiary" style={styles.colTertiary} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.tertiary} filterValue={columnFilters.tertiary ?? []} onFilterChange={(value) => handleFilterChange("tertiary", value)} />
            <Th column="org" style={styles.colOrg} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.org} filterValue={columnFilters.org ?? []} onFilterChange={(value) => handleFilterChange("org", value)} />
            <Th column="ag" style={styles.colAg} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.ag} filterValue={columnFilters.ag ?? []} onFilterChange={(value) => handleFilterChange("ag", value)} />
            <Th column="team" style={styles.colTeam} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.team} filterValue={columnFilters.team ?? []} onFilterChange={(value) => handleFilterChange("team", value)} />
            <Th column="status" style={styles.colStatus} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} onOpenFilter={onOpenFilter} filterOptions={filterOptions.status} filterValue={columnFilters.status ?? []} onFilterChange={(value) => handleFilterChange("status", value)} />
          </View>

          {loading ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>Loading team assignments…</Text>
            </View>
          ) : pageRows.length === 0 ? (
            <View style={styles.emptyState}>
              <MaterialIcons name="search-off" size={28} color="#c0c8d4" />
              <Text style={styles.emptyStateText}>
                {hasActiveFilters
                  ? "No Information Matches the Filters Applied, Please Re-Evaluate Filters"
                  : "No rows match your search."}
              </Text>
            </View>
          ) : (
            pageRows.map((row) => {
              const isHovered = hoveredRowId === row.id;
              const isSelected = selectedRowId === row.id;
              const isInactive = row.status === "inactive";
              // For members: determine if this row belongs to their team
              const rowTeamEntry = teamsProp.find((t) => t.name === row.team);
              const isRowEditable =
                memberTeamKey === undefined ||
                (rowTeamEntry !== undefined && rowTeamEntry.key === memberTeamKey);
              return (
                <Pressable
                  key={row.id}
                  onPress={() => handleSelectRow(row)}
                  disabled={readOnly || !isRowEditable}
                  // @ts-ignore — web-only hover handlers, no-op on native
                  onMouseEnter={() => setHoveredRowId(row.id)}
                  // @ts-ignore
                  onMouseLeave={() => setHoveredRowId(null)}
                  style={[
                    styles.tableBodyRow,
                    isHovered && styles.tableBodyRowHovered,
                    isSelected && styles.tableBodyRowEditing,
                    isInactive && styles.tableBodyRowInactive,
                  ]}
                >
                  <Text style={[styles.tdCell, styles.colCategory, styles.tdCellStrong]}>{row.category}</Text>
                  <Text style={[styles.tdCell, styles.colSubcategory]}>{row.subcategory}</Text>
                  <Text style={[styles.tdCell, styles.colTertiary]}>{row.tertiary || "—"}</Text>
                  <Text style={[styles.tdCell, styles.colOrg]}>{row.org}</Text>
                  <Text style={[styles.tdCell, styles.colAg]}>{row.ag || "—"}</Text>
                  {/* Team Assignment — pill chip matching the Manage Teams modal */}
                  <View style={[styles.colTeam, styles.toggleCell]}>
                    {(() => {
                      const entry = teamsProp.find((t) => t.name === row.team);
                      const color = entry?.color ?? "#838383";
                      return row.team ? (
                        <View>
                          <View style={[styles.teamPill, styles.teamPillCompact, { backgroundColor: color + "26" }]}>
                            <View style={[styles.teamDot, { backgroundColor: color }]} />
                            <Text style={[styles.teamPillText, styles.teamPillTextCompact, { color }]}>{row.team}</Text>
                          </View>
                          {row.teamAssignmentInactive && (
                            <Text style={styles.teamInactiveBadge}>
                              Team Assignment Inactive, Reactivate Team or Change Team Assignment
                            </Text>
                          )}
                        </View>
                      ) : (
                        <Text style={styles.tdCell}>—</Text>
                      );
                    })()}
                  </View>
                  <View style={[styles.colStatus, styles.toggleCell]}>
                    <StatusToggle
                      active={row.status === "active"}
                      disabled={
                        !onToggleStatus ||
                        !isRowEditable ||
                        (row.teamAssignmentInactive && row.status === "inactive")
                      }
                      onToggle={() => {
                        if (isRowEditable) onToggleStatus?.(row, row.status === "active" ? "inactive" : "active");
                      }}
                    />
                  </View>
                </Pressable>
              );
            })
          )}
        </View>
      </ScrollView>

      {/* ── Pagination ── */}
      <View style={styles.pagination}>
        <View style={styles.rowsPerPage}>
          <Text style={styles.rowsPerPageLabel}>Rows per Page</Text>
          {/* Rows-per-page dropdown */}
          <View>
            <Pressable
              style={styles.rowsPerPageSelectWrap}
              onPress={() => setRppOpen((o) => !o)}
              // @ts-ignore — web-only
              accessibilityRole="combobox"
            >
              <Text style={styles.rowsPerPageSelectText}>{rowsPerPage}</Text>
              <MaterialIcons
                name={rppOpen ? "arrow-drop-up" : "arrow-drop-down"}
                size={16}
                color="#4B4B4B"
              />
            </Pressable>
            {rppOpen && (
              <>
                {/* invisible backdrop to dismiss on outside click */}
                <Pressable
                  // @ts-ignore — web-only fixed overlay
                  style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                  onPress={() => setRppOpen(false)}
                />
                <View style={styles.rppDropdown}>
                  {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                    <Pressable
                      key={opt}
                      style={[styles.rppOption, opt === rowsPerPage && styles.rppOptionActive]}
                      onPress={() => { handleSetRowsPerPage(opt); setRppOpen(false); }}
                    >
                      <Text style={[styles.rppOptionText, opt === rowsPerPage && styles.rppOptionActiveText]}>
                        {opt}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </>
            )}
          </View>
        </View>

        <View style={styles.pageBtns}>
          <Pressable
            disabled={currentPage <= 1}
            style={[styles.pgButton, currentPage <= 1 && styles.pgButtonDisabled]}
            onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <MaterialIcons name="chevron-left" size={16} color="#4B4B4B" />
          </Pressable>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Pressable
              key={page}
              style={[styles.pgButton, page === currentPage && styles.pgButtonActive]}
              onPress={() => setCurrentPage(page)}
            >
              <Text style={[styles.pgButtonText, page === currentPage && styles.pgButtonActiveText]}>{page}</Text>
            </Pressable>
          ))}
          <Pressable
            disabled={currentPage >= totalPages}
            style={[styles.pgButton, currentPage >= totalPages && styles.pgButtonDisabled]}
            onPress={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <MaterialIcons name="chevron-right" size={16} color="#4B4B4B" />
          </Pressable>
        </View>
      </View>
      {/* Manage Teams Modal */}
      <ManageTeamsModal
        visible={manageTeamsVisible}
        initialTeams={teamsProp}
        onSave={handleSaveTeams}
        onClose={() => setManageTeamsVisible(false)}
      />

      {/* Edit Assignment Modal */}
      <EditAssignmentModal
        visible={!!editingRow}
        row={editingRow}
        teams={teamsProp}
        onClose={() => setEditingRow(null)}
        onSave={async (updates) => {
          if (editingRow && onEditAssignment) {
            await onEditAssignment(editingRow, updates);
            toast.show("Save Successful", "success");
          }
          setEditingRow(null);
        }}
      />
    </View>
  );
};

export default TeamAssignment;
